import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export type ProgramCategory =
  | 'incentives'
  | 'finance_lease'
  | 'sales_bulletins'
  | 'internal_promotions'
  | 'sales_contests'
  | 'manufacturer_updates';

export interface ProgramHighlights {
  rates?: string[];
  rebates?: string[];
  deadlines?: string[];
  what_changed?: string[];
  key_points?: string[];
}

export interface Program {
  id: string;
  dealership_id: string;
  title: string;
  category: ProgramCategory;
  summary: string;
  keywords: string[];
  effective_date: string | null;
  expiry_date: string | null;
  uploaded_by: string;
  uploaded_by_name?: string;
  file_path: string;
  file_size: number | null;
  ai_highlights: ProgramHighlights;
  processing_status: 'pending' | 'ready' | 'failed';
  created_at: string;
  updated_at: string;
  best_for?: string | null;
  talk_track?: string | null;
  objection_handling?: string | null;
  urgency_hook?: string | null;
}

export const CATEGORY_LABELS: Record<ProgramCategory, string> = {
  incentives: 'Incentives',
  finance_lease: 'Finance & Lease',
  sales_bulletins: 'Sales Bulletins',
  internal_promotions: 'Internal Promotions',
  sales_contests: 'Sales Contests',
  manufacturer_updates: 'Manufacturer Updates',
};

export const CATEGORY_ORDER: ProgramCategory[] = [
  'incentives',
  'finance_lease',
  'sales_bulletins',
  'internal_promotions',
  'sales_contests',
  'manufacturer_updates',
];

export type ProgramStatus = 'active' | 'expiring_soon' | 'expired';

export function programStatus(p: Pick<Program, 'expiry_date'>): ProgramStatus {
  if (!p.expiry_date) return 'active';
  const now = new Date();
  const exp = new Date(p.expiry_date);
  const days = Math.floor((exp.getTime() - now.getTime()) / 86400000);
  if (days < 0) return 'expired';
  if (days <= 14) return 'expiring_soon';
  return 'active';
}

const today = () => new Date().toISOString().slice(0, 10);
const offsetDays = (d: number) =>
  new Date(Date.now() + d * 86400000).toISOString().slice(0, 10);
const isoDays = (d: number) =>
  new Date(Date.now() - d * 86400000).toISOString();

const MOCK_PROGRAMS: Program[] = [
  {
    id: 'prog-mock-1',
    dealership_id: 'demo-dealership',
    title: 'November Cash Incentives — All Models',
    category: 'incentives',
    summary:
      'Up to $4,000 cash back on select 2024 inventory. Stackable with loyalty rebate. Deadline end of month.',
    keywords: ['cash back', '2024', 'loyalty', 'stackable', 'november'],
    effective_date: offsetDays(-10),
    expiry_date: offsetDays(8),
    uploaded_by: 'mgr-001',
    uploaded_by_name: 'Sarah Manager',
    file_path: 'demo/november-incentives.pdf',
    file_size: 184320,
    ai_highlights: {
      rates: ['$4,000 cash back on 2024 inventory'],
      rebates: ['$1,000 loyalty rebate (stackable)'],
      deadlines: [`Ends ${offsetDays(8)}`],
      what_changed: ['Loyalty rebate is now stackable with cash incentive'],
      key_points: ['All trims qualify', 'Excludes special editions'],
    },
    processing_status: 'ready',
    created_at: isoDays(3),
    updated_at: isoDays(3),
    best_for: 'Payment-conscious buyers eyeing 2024 inventory',
    talk_track: '"You can stack the loyalty rebate on top of $4,000 cash back — most customers don\'t realize that."',
    objection_handling: '"Price seems firm" → walk through stackable rebates to show real out-the-door price.',
    urgency_hook: 'Ends end of month — locked-in cash incentive disappears.',
  },
  {
    id: 'prog-mock-2',
    dealership_id: 'demo-dealership',
    title: 'SUV Lease Programs — 36 Month Special',
    category: 'finance_lease',
    summary:
      '$0 down, 1.9% APR on 36-month leases for mid-size SUVs. $399/mo on lead trim with $2,500 cap reduction.',
    keywords: ['lease', 'SUV', '36 month', '1.9%', '$0 down'],
    effective_date: offsetDays(-5),
    expiry_date: offsetDays(25),
    uploaded_by: 'mgr-001',
    uploaded_by_name: 'Sarah Manager',
    file_path: 'demo/suv-lease.pdf',
    file_size: 211000,
    ai_highlights: {
      rates: ['1.9% APR on 36-month leases'],
      rebates: ['$2,500 lease cap reduction'],
      deadlines: [`Program ends ${offsetDays(25)}`],
      what_changed: ['APR dropped from 2.4% to 1.9% this month'],
      key_points: ['Mid-size SUVs only', 'Tier 1 credit required'],
    },
    processing_status: 'ready',
    created_at: isoDays(5),
    updated_at: isoDays(2),
    best_for: 'SUV shoppers focused on monthly payment, not total price',
    talk_track: '"$399/mo with $0 down on a mid-size SUV — that\'s payment territory most expect on a sedan."',
    objection_handling: '"I want to buy not lease" → frame 36-month lease as flexibility + lower commitment.',
    urgency_hook: '1.9% APR is the lowest this year — likely returns to 2.4% next month.',
  },
  {
    id: 'prog-mock-3',
    dealership_id: 'demo-dealership',
    title: 'Q4 Sales Contest — Top Closer Bonus',
    category: 'sales_contests',
    summary:
      'Top 3 closers each month win cash bonuses ($1,000/$500/$250). Tiebreaker: CSI score.',
    keywords: ['contest', 'bonus', 'closer', 'CSI', 'Q4'],
    effective_date: offsetDays(-30),
    expiry_date: offsetDays(45),
    uploaded_by: 'mgr-001',
    uploaded_by_name: 'Sarah Manager',
    file_path: 'demo/q4-contest.pdf',
    file_size: 92000,
    ai_highlights: {
      rebates: ['$1,000 / $500 / $250 monthly bonuses'],
      deadlines: ['Resets monthly through end of Q4'],
      key_points: ['CSI is tiebreaker', 'Must hit minimum 8 deliveries'],
    },
    processing_status: 'ready',
    created_at: isoDays(30),
    updated_at: isoDays(30),
  },
  {
    id: 'prog-mock-4',
    dealership_id: 'demo-dealership',
    title: 'Internal SPIFF — Aged Inventory 90+ Days',
    category: 'internal_promotions',
    summary:
      '$300 SPIFF for any unit aged 90+ days. $500 for 120+. Pays at delivery, no claw-back.',
    keywords: ['spiff', 'aged', 'inventory', '90 day', 'internal'],
    effective_date: offsetDays(-7),
    expiry_date: offsetDays(3),
    uploaded_by: 'mgr-001',
    uploaded_by_name: 'Sarah Manager',
    file_path: 'demo/aged-spiff.pdf',
    file_size: 64000,
    ai_highlights: {
      rebates: ['$300 (90+ days)', '$500 (120+ days)'],
      deadlines: [`Ends ${offsetDays(3)} — expiring soon`],
      key_points: ['Pays at delivery', 'No claw-back on returns'],
    },
    processing_status: 'ready',
    created_at: isoDays(7),
    updated_at: isoDays(1),
  },
  {
    id: 'prog-mock-5',
    dealership_id: 'demo-dealership',
    title: 'Manufacturer Bulletin — New Model Year Pricing',
    category: 'manufacturer_updates',
    summary:
      'MY25 pricing released. Average MSRP increase 2.8%. New trim packages on sedans.',
    keywords: ['MSRP', 'pricing', 'MY25', 'trim', 'bulletin'],
    effective_date: offsetDays(-2),
    expiry_date: null,
    uploaded_by: 'mgr-001',
    uploaded_by_name: 'Sarah Manager',
    file_path: 'demo/my25-pricing.pdf',
    file_size: 318000,
    ai_highlights: {
      what_changed: ['Avg 2.8% MSRP increase', 'New Premium+ trim on sedans'],
      key_points: ['Effective immediately for new orders'],
    },
    processing_status: 'ready',
    created_at: isoDays(2),
    updated_at: isoDays(2),
  },
];

export function usePrograms() {
  const { mode, dealershipId } = useAuth();
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (mode === 'demo' || !dealershipId) {
      setPrograms(MOCK_PROGRAMS);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from('programs')
      .select('*, profiles:uploaded_by(full_name)')
      .eq('dealership_id', dealershipId)
      .order('updated_at', { ascending: false });
    if (!error && data) {
      setPrograms(
        (data as any[]).map(p => ({
          ...p,
          uploaded_by_name: p.profiles?.full_name ?? 'Manager',
        })) as Program[],
      );
    }
    setLoading(false);
  }, [mode, dealershipId]);

  useEffect(() => { refresh(); }, [refresh]);

  return { programs, loading, refresh };
}

export function getSignedUrl(filePath: string) {
  return supabase.storage.from('program-files').createSignedUrl(filePath, 60 * 10);
}